import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SettingsInputAntennaOutlinedIcon from "@mui/icons-material/SettingsInputAntennaOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid2,
	TextField,
	Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import {
	DetailBody,
	DetailHeader,
	DetailSection,
} from "@/controls/detail-panel/DetailPanel";
import { EDITABLE_FIELD_SX } from "@/pages/reports/rollshop-operations/components/compactDialog";
import { ReadOnlyField } from "@/pages/reports/rollshop-operations/components/ReadOnlyField";
import type { StraightenerParameterRow } from "../models/StraightenerParamLimit";

interface Props {
	row: StraightenerParameterRow | null;
	isSaving: boolean;
	onSave: (
		code: string,
		min: number | null,
		max: number | null,
		sendToPlc: boolean | null,
		isRequired: boolean | null,
	) => void;
	onClose: () => void;
	// Reports unsaved edits so the manager can guard row navigation.
	onDirtyChange?: (dirty: boolean) => void;
}

const numOrNull = (s: string): number | null =>
	s.trim() === "" ? null : Number(s);

export const StraightenerLimitDetailViewer = ({
	row,
	isSaving,
	onSave,
	onClose,
	onDirtyChange,
}: Props) => {
	const [min, setMin] = useState("");
	const [max, setMax] = useState("");
	const [sendToPlc, setSendToPlc] = useState(false);
	const [required, setRequired] = useState(false);

	useEffect(() => {
		setMin(row?.min != null ? String(row.min) : "");
		setMax(row?.max != null ? String(row.max) : "");
		setSendToPlc(row?.sendToPlc ?? false);
		setRequired(row?.isRequired ?? false);
	}, [row?.code, row?.min, row?.max, row?.sendToPlc, row?.isRequired]);

	const dirty =
		row != null &&
		(min !== (row.min != null ? String(row.min) : "") ||
			max !== (row.max != null ? String(row.max) : "") ||
			sendToPlc !== row.sendToPlc ||
			required !== row.isRequired);

	useEffect(() => {
		onDirtyChange?.(dirty);
	}, [dirty, onDirtyChange]);

	if (!row) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
					color: "text.secondary",
				}}
			>
				<Typography>Select a parameter to edit its configuration</Typography>
			</Box>
		);
	}

	// Read-back params are display only: never sent, never required.
	const canToggleFlags = !row.readOnly;
	// Business rule: sent to the PLC => required (the backend enforces it too).
	const requiredLocked = sendToPlc;
	// Numeric limits only make sense for editable numeric parameters.
	const canEditLimits = row.valueType === "numeric" && !row.readOnly;

	const minN = numOrNull(min);
	const maxN = numOrNull(max);
	const invalid = minN != null && maxN != null && minN >= maxN;

	const limitField = (
		label: string,
		value: string,
		onChange: (value: string) => void,
		error?: boolean,
		helperText?: string,
	) =>
		canEditLimits ? (
			<TextField
				label={label}
				type="number"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				error={error}
				helperText={helperText}
				fullWidth
				size="small"
				sx={EDITABLE_FIELD_SX}
				slotProps={{
					inputLabel: { shrink: true },
					htmlInput: {
						style: { textAlign: "right", fontVariantNumeric: "tabular-nums" },
					},
				}}
			/>
		) : (
			<ReadOnlyField label={label} value={value} numeric />
		);

	return (
		<Box
			sx={{
				height: "100%",
				display: "flex",
				flexDirection: "column",
				overflow: "hidden",
			}}
		>
			<DetailHeader
				title={row.displayName}
				isSaving={isSaving}
				onClose={onClose}
				actions={
					<Button
						variant="contained"
						color="primary"
						size="small"
						onClick={() =>
							onSave(
								row.code,
								minN,
								maxN,
								canToggleFlags ? sendToPlc : null,
								canToggleFlags ? required : null,
							)
						}
						disabled={isSaving || invalid || !dirty}
					>
						SAVE
					</Button>
				}
			/>
			<DetailBody>
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						overflow: "auto",
						display: "flex",
						flexDirection: "column",
						gap: 0.75,
					}}
				>
					<DetailSection title="Parameter" icon={<InfoOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								<ReadOnlyField label="Section" value={row.group} />
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection title="Validation Limits" icon={<TuneOutlinedIcon />}>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{limitField("Min", min, setMin)}
							</Grid2>
							<Grid2 size={{ xs: 12, md: 6 }}>
								{limitField(
									"Max",
									max,
									setMax,
									invalid,
									invalid ? "Max must be greater than Min" : undefined,
								)}
							</Grid2>
						</Grid2>
					</DetailSection>

					<DetailSection
						title="PLC"
						icon={<SettingsInputAntennaOutlinedIcon />}
					>
						<Grid2 container spacing={1}>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={sendToPlc}
											onChange={(e) => {
												setSendToPlc(e.target.checked);
												if (e.target.checked) setRequired(true);
											}}
											disabled={!canToggleFlags}
											size="small"
										/>
									}
									label="Send to PLC"
									sx={{ ml: 0 }}
								/>
							</Grid2>
							<Grid2 size={{ xs: 6, md: 3 }}>
								<FormControlLabel
									control={
										<Checkbox
											checked={required}
											onChange={(e) => setRequired(e.target.checked)}
											disabled={!canToggleFlags || requiredLocked}
											size="small"
										/>
									}
									label="Required"
									sx={{ ml: 0 }}
								/>
							</Grid2>
						</Grid2>
					</DetailSection>
				</Box>
			</DetailBody>
		</Box>
	);
};
